import type { FragmentType } from 'src/gql';
import { useRef, useState } from 'react';
import NextLink from 'next/link';
import { useApolloClient } from '@apollo/client';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@savvy-kit/components/dialog';
import IconButton from '@savvy-kit/components/icon-button';
import { Popover, PopoverContent, PopoverTrigger } from '@savvy-kit/components/popover';
import { Text } from '@savvy-kit/foundations/text';
import { Archive, ArrowUpRight, Clock } from 'lucide-react';
import { toast } from 'sonner';
import { graphql, unmaskFragment } from 'src/gql';
import {
  alertDateFormatted,
  getAlertContent,
  getAlertDisplayName,
  getAlertLink,
  getReminderDate,
} from 'src/utils/action-center';

import { ALERT_STATUS_BACKLOGGED, ALERT_STATUS_REMOVED } from '@savvy/constants/alert-statuses';
import { useMutationWithErrorLogging } from '@savvy/graphql-hooks/useMutationWithErrorLogging';

import { InboxGroupMemberFragment } from './inbox-group-fragment';

type Props = {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly title: string;
  readonly members: readonly FragmentType<typeof InboxGroupMemberFragment>[];
};

// Own mutation (distinct operation name) so the dialog stays independent of inbox-card — the card
// already imports this dialog, so importing its mutation back would form a cycle (the reason
// InboxGroupMemberFragment lives in its own file).
const updateGroupMemberAlertMutation = graphql(/* GraphQL */ `
  mutation UpdateGroupMemberAlert($input: UpdateAlertInput!) {
    updateAlert(input: $input) {
      alert {
        id
      }
    }
  }
`);

const SNOOZE_PRESETS = [
  { label: 'Tomorrow', amount: 1, unit: 'day' },
  { label: '1 week', amount: 7, unit: 'day' },
  { label: '2 weeks', amount: 14, unit: 'day' },
  { label: '1 month', amount: 1, unit: 'month' },
  { label: '3 months', amount: 3, unit: 'month' },
] as const;

function MemberActions({
  alertId,
  onActioned,
  onActionStart,
}: {
  readonly alertId: string;
  readonly onActioned: (id: string) => void;
  readonly onActionStart: (promise: Promise<unknown>) => void;
}) {
  const [snoozeOpen, setSnoozeOpen] = useState(false);
  // Deliberately omit 'NotificationsV2Inbox' here: refetching it re-derives the group and remounts
  // this card, closing the dialog mid-triage (UNO-972). The list is reconciled once on close.
  const [updateAlert] = useMutationWithErrorLogging(updateGroupMemberAlertMutation, {
    refetchQueries: ['NotificationsPeekList', 'NotificationsMenuButton'],
  });

  const archive = () => {
    const promise = updateAlert({ variables: { input: { id: alertId, status: ALERT_STATUS_REMOVED } } });
    onActionStart(promise);
    promise
      .then(() => {
        toast.success('Moved to Archive');
        onActioned(alertId);
      })
      .catch((err: unknown) => console.error('[UNO-588] failed to archive group member', err));
  };

  const snooze = (amount: number, unit: string) => {
    const date = getReminderDate(amount, unit);
    const promise = updateAlert({
      variables: {
        input: {
          id: alertId,
          status: ALERT_STATUS_BACKLOGGED,
          surfaceAt: date.toISOString(),
          snoozedAt: new Date().toISOString(),
        },
      },
    });
    onActionStart(promise);
    promise
      .then(() => {
        toast.success(`Snoozed until ${alertDateFormatted(date)}`);
        setSnoozeOpen(false);
        onActioned(alertId);
      })
      .catch((err: unknown) => console.error('[UNO-588] failed to snooze group member', err));
  };

  return (
    <div className="flex shrink-0 items-center gap-0.5">
      <Popover open={snoozeOpen} onOpenChange={setSnoozeOpen}>
        <PopoverTrigger
          render={
            <IconButton type="button" variant="ghost" size="sm" aria-label="Snooze">
              <Clock className="size-4" />
            </IconButton>
          }
        />
        <PopoverContent align="end" className="flex w-56 flex-col p-0">
          <Text className="px-3 pt-3 pb-1 text-xs text-secondary">Snooze until</Text>
          {SNOOZE_PRESETS.map((option) => (
            <button
              key={option.label}
              type="button"
              className="px-3 py-2 text-left text-sm hover:bg-component-hover"
              onClick={() => snooze(option.amount, option.unit)}
            >
              {option.label}
            </button>
          ))}
        </PopoverContent>
      </Popover>
      <IconButton type="button" variant="ghost" size="sm" aria-label="Archive" onClick={archive}>
        <Archive className="size-4" />
      </IconButton>
    </div>
  );
}

// The "View all N" list behind a collapsed bulk card — one row per member alert. Each row
// deep-links to its target and offers per-member snooze/archive, like a normal inbox card.
export function InboxGroupDialog({ open, onOpenChange, title, members }: Props) {
  const client = useApolloClient();
  const [removedIds, setRemovedIds] = useState<ReadonlySet<string>>(() => new Set());
  // Member mutations that haven't resolved yet. Tracked so a close mid-flight still reconciles: the
  // refetch waits for them to settle first, otherwise it could re-read the list before the server
  // has applied the archive/snooze and leave the inbox stale. (UNO-972)
  const pendingActions = useRef<Set<Promise<unknown>>>(new Set());

  const trackAction = (promise: Promise<unknown>) => {
    pendingActions.current.add(promise);
    void promise.finally(() => pendingActions.current.delete(promise));
  };

  const visibleMembers = members
    .map((memberFrag) => unmaskFragment(InboxGroupMemberFragment, memberFrag))
    .filter((member) => !removedIds.has(member.id));

  const handleOpenChange = (next: boolean) => {
    if (!next) {
      // The per-member actions skipped the inbox refetch to stay open; reconcile the list now, once
      // any still-in-flight mutations have settled.
      if (removedIds.size > 0 || pendingActions.current.size > 0) {
        void Promise.allSettled([...pendingActions.current]).then(() =>
          client.refetchQueries({ include: ['NotificationsV2Inbox'] }),
        );
      }
      setRemovedIds(new Set());
    }
    onOpenChange(next);
  };

  // Never auto-close on a member action — the point of the dialog is to stay open for triage, even
  // once a single (or the last) member remains. It closes only when the user dismisses it, and
  // handleOpenChange reconciles the inbox then. (UNO-972)
  const handleMemberActioned = (id: string) => {
    setRemovedIds((prev) => new Set(prev).add(id));
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="flex max-h-[80vh] flex-col gap-0 overflow-hidden p-0 sm:max-w-lg">
        <DialogHeader className="border-b border-light px-6 pt-6 pb-4 text-left">
          <DialogTitle className="text-base font-semibold">{title}</DialogTitle>
          <DialogDescription className="text-sm text-secondary">
            {visibleMembers.length} {visibleMembers.length === 1 ? 'item' : 'items'}
          </DialogDescription>
        </DialogHeader>
        {visibleMembers.length === 0 && (
          <div className="flex flex-1 items-center justify-center px-6 py-12">
            <Text className="text-sm text-secondary">You&apos;re all caught up here.</Text>
          </div>
        )}
        <ul className="min-h-0 flex-1 list-none divide-y divide-light overflow-y-auto">
          {visibleMembers.map((member) => {
            const primary = getAlertDisplayName(member);
            const secondary = getAlertContent(member, { useActorLedContent: true }) || '';
            const href = getAlertLink(member);
            const clickable = typeof href === 'string' && href.length > 0;
            const label = (
              <div className="min-w-0">
                <Text className="block break-words text-sm font-medium">{primary}</Text>
                <Text className="block break-words text-xs text-secondary">{secondary}</Text>
              </div>
            );
            return (
              <li
                key={member.id}
                className="flex items-center justify-between gap-3 px-6 py-3 transition-colors hover:bg-component-hover"
              >
                {clickable ? (
                  <NextLink
                    href={href}
                    target="_blank"
                    rel="noreferrer"
                    className="flex min-w-0 flex-1 items-center gap-3"
                  >
                    {label}
                    <ArrowUpRight aria-hidden="true" className="size-4 shrink-0 text-secondary" />
                  </NextLink>
                ) : (
                  <div className="flex min-w-0 flex-1 items-center gap-3">{label}</div>
                )}
                <MemberActions alertId={member.id} onActioned={handleMemberActioned} onActionStart={trackAction} />
              </li>
            );
          })}
        </ul>
      </DialogContent>
    </Dialog>
  );
}
